import { Code2, Smartphone, Bot, ArrowRight } from "lucide-react";

const CORE_CAPABILITIES = [
  {
    id: "software",
    icon: Code2,
    title: "Software Development",
    tagline: "Custom platforms · CRM · ERP · APIs",
    image: "/images/core/software.jpg",
    alt: "Abstract code and software architecture representing ClomedixTech custom software development.",
    bullets: [
      "Full-stack web platforms (React, Node.js, Python, .NET)",
      "Custom CRM, ERP and internal tools",
      "API design, integrations and system automation",
      "Cloud-native architecture on AWS / Azure",
    ],
  },
  {
    id: "app",
    icon: Smartphone,
    title: "Application Development",
    tagline: "iOS · Android · Flutter · React Native",
    image: "/images/core/app.jpg",
    alt: "Mobile phone showing a modern app interface — ClomedixTech mobile application development.",
    bullets: [
      "Native iOS & Android apps and cross-platform builds",
      "Progressive Web Apps and offline-first experiences",
      "Payments, push notifications, deep-linking, analytics",
      "App-store submission, review handling and updates",
    ],
  },
  {
    id: "ai-agent",
    icon: Bot,
    title: "AI Agent Development",
    tagline: "Autonomous agents · RAG · multi-agent systems",
    image: "/images/core/ai-agent.jpg",
    alt: "Neural network / AI robot visual — ClomedixTech AI agent development.",
    bullets: [
      "24/7 AI concierge chatbots trained on your docs",
      "Retrieval-Augmented Generation (RAG) knowledge bases",
      "Multi-agent frameworks for research, sales and support",
      "AI meeting schedulers, doc intelligence and workflow bots",
    ],
  },
];

export const CoreCapabilities = () => (
  <section id="core" className="py-24 lg:py-32 bg-slate-50" data-testid="core-capabilities-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="max-w-3xl reveal">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-purple">Core Capabilities</div>
        <h2 className="section-heading text-3xl sm:text-4xl lg:text-5xl mt-3 text-slate-900">
          Three engineering pillars, <span className="text-gradient">one team</span>
        </h2>
        <p className="mt-4 text-slate-600 text-lg max-w-2xl">
          Deep specialisation across the three areas that most modern businesses need to move forward — custom software, mobile applications, and autonomous AI agents.
        </p>
      </div>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
        {CORE_CAPABILITIES.map((cap, i) => {
          const Icon = cap.icon;
          return (
            <div
              key={cap.id}
              className="group card-lift card-tilt holo-card rounded-3xl border border-slate-100 soft-shadow overflow-hidden flex flex-col reveal"
              style={{ transitionDelay: `${i * 100}ms` }}
              data-testid={`core-card-${cap.id}`}
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={cap.image}
                  alt={cap.alt}
                  loading="lazy"
                  width="1200"
                  height="600"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white via-white/25 to-transparent" />
                <div className="absolute top-4 left-4 w-12 h-12 rounded-2xl bg-white/95 backdrop-blur flex items-center justify-center text-brand-purple shadow-lg">
                  <Icon className="w-6 h-6" />
                </div>
              </div>

              <div className="p-7 flex flex-col flex-1">
                <h3 className="font-heading font-extrabold text-2xl text-slate-900">{cap.title}</h3>
                <p className="text-sm text-brand-purple mt-1 font-semibold">{cap.tagline}</p>
                <ul className="mt-5 space-y-2.5 text-sm text-slate-700 flex-1">
                  {cap.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2.5">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-gradient shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <a href="#contact" className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-slate-900 group-hover:text-brand-purple transition-colors" data-testid={`core-cta-${cap.id}`}>
                  Discuss your project <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);
